import { useCallback, useRef, useState } from 'react'
import { FileText, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/shared/lib/utils'
import type { RohsComponentEvidenceSlot } from '@/shared/hooks/api/rohsComplianceFiles'
import { useUploadCatalogComponentComplianceSlot } from '@/shared/hooks/api/rohsComplianceFiles'
import { Button } from '@/components/ui/button'
import {
  Attachment,
  AttachmentContent,
  AttachmentMedia,
  AttachmentTitle,
} from '@/components/ui/attachment'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export function V2ComponentRohsComplianceSlotFileDialog({
  componentId,
  slotKind,
  fileName,
  open,
  onOpenChange,
}: {
  componentId: string
  slotKind: RohsComponentEvidenceSlot
  fileName: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('common')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const { mutateAsync: uploadSlot } =
    useUploadCatalogComponentComplianceSlot(componentId)
  const hasFile = fileName != null
  const slotTitle = t(
    `v2.pages.products.detail.rohsFiles.componentFile.slots.${slotKind}`
  )

  let title = t(
    'v2.pages.products.detail.rohsFiles.componentFile.fileDialog.uploadTitle',
    { slotTitle }
  )
  const slotUploadDescriptionKey = `v2.pages.products.detail.rohsFiles.componentFile.fileDialog.uploadDescriptions.${slotKind}`
  let description = t(slotUploadDescriptionKey, {
    defaultValue: t(
      'v2.pages.products.detail.rohsFiles.componentFile.fileDialog.uploadDescription'
    ),
  })
  let chooseLabel = t(
    'v2.pages.products.detail.rohsFiles.componentFile.fileDialog.selectFile'
  )
  if (hasFile) {
    title = t(
      'v2.pages.products.detail.rohsFiles.componentFile.fileDialog.editTitle',
      { slotTitle }
    )
    description = t(
      'v2.pages.products.detail.rohsFiles.componentFile.fileDialog.editDescription'
    )
    chooseLabel = t(
      'v2.pages.products.detail.rohsFiles.componentFile.fileDialog.replaceFile'
    )
  }

  const handleFile = useCallback(
    async (file: File) => {
      setIsUploading(true)
      try {
        await uploadSlot({ slotKind, file })
        onOpenChange(false)
      } catch {
        toast.error(
          t(
            'v2.pages.products.detail.rohsFiles.componentFile.actions.uploadFailed'
          )
        )
      } finally {
        setIsUploading(false)
      }
    },
    [onOpenChange, slotKind, t, uploadSlot]
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <p className='text-sm text-muted-foreground'>{description}</p>
        </DialogHeader>
        {hasFile ? (
          <div className='space-y-1'>
            <p className='text-xs font-medium text-muted-foreground'>
              {t(
                'v2.pages.products.detail.rohsFiles.componentFile.fileDialog.currentFile'
              )}
            </p>
            <Attachment state='done' size='sm' className='w-full'>
              <AttachmentMedia>
                <FileText aria-hidden />
              </AttachmentMedia>
              <AttachmentContent>
                <AttachmentTitle title={fileName}>{fileName}</AttachmentTitle>
              </AttachmentContent>
            </Attachment>
          </div>
        ) : null}
        <div
          className={cn(
            'flex min-h-[160px] flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center',
            'border-muted-foreground/25 hover:border-muted-foreground/50',
            isUploading && 'pointer-events-none opacity-60'
          )}
          onDragOver={(event) => {
            event.preventDefault()
          }}
          onDrop={(event) => {
            event.preventDefault()
            const file = event.dataTransfer.files[0]
            if (file != null) {
              void handleFile(file)
            }
          }}
        >
          <Upload className='mb-2 size-10 text-muted-foreground' />
          <p className='mb-3 text-sm text-muted-foreground'>
            {t(
              'v2.pages.products.detail.rohsFiles.componentFile.fileDialog.dragDrop'
            )}
          </p>
          <p className='mb-4 text-xs text-muted-foreground'>
            {t(
              'v2.pages.products.detail.rohsFiles.componentFile.fileDialog.orClick'
            )}
          </p>
          <input
            ref={fileInputRef}
            type='file'
            className='sr-only'
            disabled={isUploading}
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file != null) {
                void handleFile(file)
              }
              event.target.value = ''
            }}
          />
          <Button
            disabled={isUploading}
            variant='secondary'
            size='sm'
            onClick={() => {
              fileInputRef.current?.click()
            }}
          >
            {chooseLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
